const AUTH_RETURN_KEY = 'store:jam-auth-return'
const AUTH_RETURN_TTL_MS = 15 * 60 * 1000

export const jamParams = (search: string) => {
  const params = new URLSearchParams(search)
  const recordingParams = new URLSearchParams()
  params.forEach((value, key) => {
    if (key.startsWith('jam-')) recordingParams.append(key, value)
  })
  return recordingParams
}

export const mergedJamSearch = (previousSearch: string, currentSearch: string) => {
  const merged = jamParams(previousSearch)
  const current = jamParams(currentSearch)
  new Set(current.keys()).forEach(key => merged.delete(key))
  current.forEach((value, key) => merged.append(key, value))
  return merged.toString() ? `?${merged.toString()}` : ''
}

export const withJamParams = (target: string, sourceSearch: string) => {
  const recordingParams = jamParams(sourceSearch)
  if (!recordingParams.toString()) return target

  let url: URL
  try {
    url = new URL(target, window.location.origin)
  } catch {
    return target
  }
  if (url.origin !== window.location.origin) return target

  const recordingKeys = new Set(recordingParams.keys())
  recordingKeys.forEach(key => url.searchParams.delete(key))
  recordingParams.forEach((value, key) => url.searchParams.append(key, value))
  return `${url.pathname}${url.search}${url.hash}`
}

export const loginUrlFor = (loginPath: string, destination: string) => {
  const url = new URL(loginPath, window.location.origin)
  url.searchParams.set('redirect_to', destination)
  try {
    return withJamParams(`${url.pathname}${url.search}`, new URL(destination, window.location.origin).search)
  } catch {
    return `${url.pathname}${url.search}`
  }
}

export const clearExternalAuthReturn = () => {
  try {
    sessionStorage.removeItem(AUTH_RETURN_KEY)
  } catch {
    // Storage can be disabled independently of Store authentication.
  }
}

export const rememberExternalAuthReturn = (destination: string, sourceSearch: string) => {
  try {
    if (new URL(destination, window.location.origin).origin !== window.location.origin) {
      clearExternalAuthReturn()
      return
    }
    const target = withJamParams(destination, sourceSearch)
    if (!jamParams(new URL(target, window.location.origin).search).toString()) {
      clearExternalAuthReturn()
      return
    }

    sessionStorage.setItem(AUTH_RETURN_KEY, JSON.stringify({ target, expiresAt: Date.now() + AUTH_RETURN_TTL_MS }))
  } catch {
    clearExternalAuthReturn()
    // Invalid redirects or unavailable storage must not prevent external sign-in.
  }
}

export const takeExternalAuthReturn = () => {
  try {
    const saved = sessionStorage.getItem(AUTH_RETURN_KEY)
    clearExternalAuthReturn()
    if (!saved) return null
    const { target, expiresAt } = JSON.parse(saved)
    const url = new URL(target, window.location.origin)
    if (expiresAt < Date.now() || url.origin !== window.location.origin) return null
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return null
  }
}

export const externalAuthReturnUrl = (fallback: string, callbackSearch: string) =>
  withJamParams(takeExternalAuthReturn() || fallback, callbackSearch)

export const directSsoReturnUrl = (callbackSearch: string) => {
  clearExternalAuthReturn()
  return `/${callbackSearch}`
}
